import { CommercialMeterKey } from "@/modules/commercial-plans";

export interface CommercialUsageLimitCopy {
  title: string;
  body: string;
  recovery: string;
}

const COMMERCIAL_USAGE_LIMIT_COPY: Record<CommercialMeterKey, CommercialUsageLimitCopy> = {
  [CommercialMeterKey.UserRequests]: {
    title: "You've reached your Agent request limit",
    body: "This request was not accepted or counted.",
    recovery: "Choose a larger plan or authorize extra usage, then send it again.",
  },
  [CommercialMeterKey.NewNotes]: {
    title: "You've reached your new-note limit",
    body: "Your draft or inbound source is preserved, but Mem cannot accept another note yet.",
    recovery: "Choose a larger plan or authorize extra usage to create the note.",
  },
  [CommercialMeterKey.ActiveNotes]: {
    title: "This note was saved, but proactive processing is paused",
    body: "The note remains stored, searchable, and editable. Mem Agent is not actively processing this new change.",
    recovery:
      "Choose a larger plan or authorize extra usage. A later eligible edit can resume proactive processing when capacity is available.",
  },
  [CommercialMeterKey.OpenTasksProjects]: {
    title: "This work is saved, but monitoring is paused",
    body: "The task or project remains in Mem, but Mem Agent is not monitoring it in working memory.",
    recovery: "Complete other work, choose a larger plan, or select which work should stay active.",
  },
  [CommercialMeterKey.ActiveCustomRoutines]: {
    title: "You've reached your active-routine limit",
    body: "The routine was not activated, so it will not run in the background.",
    recovery: "Pause another custom routine, choose a larger plan, or authorize extra usage.",
  },
  [CommercialMeterKey.RoutineExecutions]: {
    title: "This routine is paused at its execution limit",
    body: "Mem preserved the routine and its next affected run instead of retrying continuously.",
    recovery: "Reduce its cadence, choose a larger plan, or authorize extra usage to resume it.",
  },
  [CommercialMeterKey.ActiveConnections]: {
    title: "You've reached your active-connection limit",
    body: "Mem did not start a new authorization. Your existing connections were not removed.",
    recovery: "Disable another connection, choose a larger plan, or authorize extra usage.",
  },
  [CommercialMeterKey.VoiceModeHours]: {
    title: "You've reached your Voice Mode allowance",
    body: "Mem finishes and saves an in-progress recording, but the next recording is paused.",
    recovery: "Choose a larger plan or authorize extra usage before starting another recording.",
  },
};

const ACCEPTED_NEW_NOTE_LIMIT_COPY: CommercialUsageLimitCopy = {
  title: "This note was saved, but proactive processing is paused",
  body: "The new note remains stored, searchable, and editable. Mem Agent is not actively processing it in the background.",
  recovery:
    "Choose a larger plan or authorize extra usage. A later eligible edit can resume proactive processing when capacity is available.",
};

export const getCommercialUsageLimitCopy = (
  meterKey: CommercialMeterKey,
  options: { accepted?: boolean } = {}
): CommercialUsageLimitCopy => {
  if (meterKey === CommercialMeterKey.NewNotes && options.accepted === true) {
    return ACCEPTED_NEW_NOTE_LIMIT_COPY;
  }

  return COMMERCIAL_USAGE_LIMIT_COPY[meterKey];
};
